import costApi from '@Apis/cost.api';
import type {
	CostTableSummary,
	CostTableDetail,
	CostTableDataEntry,
} from '@Types/cost.types';

export const costTableKeys = {
	all: ['costTables'] as const,
	list: () => [...costTableKeys.all, 'list'] as const,
	detail: (id: string) => [...costTableKeys.all, 'detail', id] as const,
	data: (id: string, dataId: string) =>
		[...costTableKeys.detail(id), 'data', dataId] as const,
};

export const costTablesListQuery = (enabled: boolean = true) => ({
	queryKey: costTableKeys.list(),
	queryFn: async () => {
		const fetchPage = async (page: number) => {
			const { data } = await costApi.get<{
				body: {
					pagination: { pages: number };
					data: CostTableSummary[];
				};
			}>('/cost-tables', {
				params: {
					page,
					slice: 100,
					sort_key: 'name',
					sort_descending: false,
				},
			});
			return data.body;
		};

		const first = await fetchPage(1);
		if (first.pagination.pages <= 1) {
			return first.data;
		}

		const remaining = await Promise.all(
			Array.from({ length: first.pagination.pages - 1 }, (_, i) =>
				fetchPage(i + 2),
			),
		);
		return [first, ...remaining].flatMap(r => r.data);
	},
	enabled,
});

export const costTableDetailQuery = (costTableId: string) => ({
	queryKey: costTableKeys.detail(costTableId),
	queryFn: async () => {
		const { data } = await costApi.get<{
			body: { cost_table: CostTableDetail };
		}>(`/cost-tables/${costTableId}`);
		return data.body.cost_table;
	},
	enabled: !!costTableId,
});

export const costTableDataQuery = (costTableId: string, dataId: string) => ({
	queryKey: costTableKeys.data(costTableId, dataId),
	queryFn: async () => {
		const { data } = await costApi.get<{
			body: { data: CostTableDataEntry[] };
		}>(`/cost-tables/${costTableId}/data/${dataId}`);
		return data.body.data ?? [];
	},
	enabled: !!costTableId && !!dataId,
});
